import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { useLogout, useMe } from '../hooks/useApi';
import { useAuth } from '../stores/auth';
import { Sidebar } from './Sidebar';
import { PreviewModeToggle } from './PreviewModeToggle';
import { CostBadge } from './CostBadge';
import { ProviderAlert } from './ProviderAlert';

export function Layout() {
  const { data: me, isLoading } = useMe();
  const setUser = useAuth((s) => s.setUser);
  const logoutMutation = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (me) setUser(me);
  }, [me, setUser]);

  useEffect(() => {
    if (!isLoading && !me) navigate('/login', { replace: true });
  }, [isLoading, me, navigate]);

  // Cerrar drawer al cambiar de ruta
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3 border-b border-slate-800 bg-slate-900 shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {/* Hamburger solo en mobile */}
          {me && (
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden w-8 h-8 rounded text-slate-400 hover:bg-slate-800 hover:text-slate-100 flex items-center justify-center text-base shrink-0"
              aria-label="Abrir menú lateral"
            >
              ☰
            </button>
          )}
          <Link to="/projects" className="flex items-center gap-1.5 min-w-0">
            <img src="/logo.png" alt="" className="h-6 w-6 sm:h-7 sm:w-7 shrink-0" />
            <span className="font-semibold text-slate-100 text-sm sm:text-base truncate">xLearn Vision</span>
          </Link>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-3 text-sm shrink-0">
          {me && (
            <>
              <CostBadge />
              <PreviewModeToggle />
              {/* Email solo en sm+ — en mobile ocupa demasiado espacio */}
              <span className="hidden sm:inline text-slate-400 text-xs lg:text-sm truncate max-w-[12rem] lg:max-w-none">
                {me.email}
              </span>
              <button
                onClick={async () => {
                  await logoutMutation.mutateAsync();
                  navigate('/login', { replace: true });
                }}
                className="px-2 sm:px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 text-xs sm:text-sm shrink-0"
                title="Cerrar sesión"
              >
                <span className="hidden sm:inline">Salir</span>
                <span className="sm:hidden">↪</span>
              </button>
            </>
          )}
        </div>
      </header>
      <div className="flex-1 flex overflow-hidden relative">
        {/* Sidebar desktop: visible md+ */}
        {me && (
          <div className="hidden md:flex">
            <Sidebar />
          </div>
        )}

        {/* Sidebar mobile: drawer overlay */}
        {me && mobileSidebarOpen && (
          <>
            <div
              className="md:hidden absolute inset-0 bg-black/60 z-40"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <div className="md:hidden absolute left-0 top-0 bottom-0 z-50 shadow-2xl">
              <Sidebar />
            </div>
          </>
        )}

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <ProviderAlert />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
